import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress, Alert, Stack, Button, TextField } from '@mui/material';
import apiService from '../../services/apiService';
import SearchIcon from '@mui/icons-material/Search';
import MachineQueueCard from '../../components/MachineQueueCard'; // Import the new component

const QueuesPage = () => {
    const [queues, setQueues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isActionLoading, setActionLoading] = useState(false);
    const [searchPlayerId, setSearchPlayerId] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const intervalRef = useRef(null);

    const fetchAllQueues = useCallback(() => {
        if (document.hidden) return;
        apiService.get('/api/queues/active')
            .then(data => setQueues(data))
            .catch(err => setError('Failed to fetch live queue data.'))
            .finally(() => setLoading(false));
    }, []);

    const handleSearch = async () => { /* ... (search logic remains the same) */ };
    const clearSearch = () => { /* ... (clear search logic remains the same) */ };

    const handleClearQueue = async (machineId) => {
        setActionLoading(true);
        try {
            await apiService.post(`/api/queues/${machineId}/clear`);
            fetchAllQueues();
        } catch (err) { setError(`Failed to clear queue for machine ${machineId}`); }
        finally { setActionLoading(false); }
    };

    const handleRemovePosition = async (machineId, position) => {
        setActionLoading(true);
        try {
            await apiService.post(`/api/queues/${machineId}/remove`, position);
            fetchAllQueues();
        } catch (err) { setError(`Failed to remove position ${position.id}`); }
        finally { setActionLoading(false); }
    };

    useEffect(() => {
        fetchAllQueues();
        intervalRef.current = setInterval(fetchAllQueues, 2000);
        return () => clearInterval(intervalRef.current);
    }, [fetchAllQueues]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>Live Machine Queues</Typography>
            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{p: 2, mb: 3}}>
                <Stack direction="row" spacing={1}>
                    <TextField label="Find Queue by Player ID" variant="outlined" size="small" fullWidth value={searchPlayerId} onChange={(e) => setSearchPlayerId(e.target.value)} />
                    <Button variant="contained" onClick={handleSearch} disabled={isSearching || !searchPlayerId}>{isSearching ? <CircularProgress size={24} /> : <SearchIcon />}</Button>
                    <Button variant="outlined" onClick={clearSearch}>Clear</Button>
                </Stack>
            </Paper>

            {queues.length === 0 && !loading ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}><Typography>No active queues found.</Typography></Paper>
            ) : (
                <Grid container spacing={3}>
                    {queues.map((queue) => (
                        <Grid item xs={12} md={6} lg={4} key={queue.machineId}>
                            <MachineQueueCard
                                machineId={queue.machineId}
                                machineName={queue.machineName}
                                categoryImageUrl={queue.categoryImageUrl}
                                players={queue.players}
                                displayMode="full"
                                onClearQueue={handleClearQueue}
                                onRemovePosition={handleRemovePosition}
                                isActionLoading={isActionLoading}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default QueuesPage;